import { cloneDeep, get } from 'lodash';
import { IAccessoryList, INextArticle, IOrder, IRammeavtale, mapVatPricesForNext } from 'next-common';
import { useEffect, useState } from 'react';
import { ArticleType } from 'sales-types-common';
import { ICalculatePricesBasedOnArticleListResult } from 'sales-common-calculations';
import { calculateVatForElCars, VehicleConfig } from '../../../../utils';
import { articlesForDiscount, DropDownValues, IDiscountFieldProps, validateDiscount } from '../MarginCartUtils';
import {
  addNewAccessoryInList,
  calculateDiscountedPrice,
  convertToCurrency,
  convertToPercentage,
  getDeliveryCharge,
  updateAccessoryList,
  updateArticles,
} from './FrameworkAgreementUtils';
import { CartPrices, CartSummaryData, formatNumber, InputType, TTagSeverity } from 'types';
import { Button, Icon, IconType, InputField, Tag } from '@repo/ui/components';

interface IFrameworkAgreement extends IDiscountFieldProps {
  order: IOrder;
  accessoryChanges?: CartSummaryData[];
  articles: INextArticle[];
  onUpdateArticles?(value: IAccessoryList, customerAdvantage?: boolean, discountArticles?: INextArticle[]): void;
  setVatPrices(vatPrices: CartPrices): void;
  carType: string;
  rammeavtale?: IRammeavtale;
  setRammeavtale?(rammeavtale: IRammeavtale): void;
  checkedAgreement?: boolean;
}

export const FrameworkAgreement = (props: IFrameworkAgreement) => {
  const [agreementNumber, setAgreementNumber] = useState(props?.order?.rammeavtale?.agreementNumber ?? '');
  const [showAccessoryList, setShowAccessoryList] = useState(false);
  const [accessoryList, setAccessoryList] = useState<IAccessoryList[]>([]);
  const [updatedFromOrder, setUpdatedFromOrder] = useState(false);
  const [initialAccessories, setInitialAccessories] = useState(false);
  const [errorMessages, setErrorMessages] = useState<Record<number, string>>({});
  const orderAccessories = cloneDeep(props?.order?.rammeavtale?.accessories);

  useEffect(() => {
    setAgreementNumber(props?.order?.rammeavtale?.agreementNumber ?? '');
  }, [props.order?.rammeavtale]);

  useEffect(() => {
    if (!props.articles || !props.order || props?.articles?.length === 0 || !props?.checkedAgreement) {
      return;
    }

    if (!updatedFromOrder && props?.order?.rammeavtale?.accessories.length > 0) {
      setAgreementNumber(props?.order?.rammeavtale?.agreementNumber);

      const filteredAccessories = props?.accessoryChanges?.filter((accessory) => {
        return (
          accessory?.key?.startsWith(VehicleConfig.ACCESSORY) ||
          accessory?.key?.startsWith(VehicleConfig.TYRE) ||
          accessory?.key?.startsWith('CUSTOM') ||
          accessory?.key?.startsWith('DARS') ||
          accessory?.key?.startsWith('FURNISHING') ||
          accessory?.key?.startsWith('SUPERSTRUCTURE') ||
          accessory?.key?.startsWith('RECONSTRUCTION')
        );
      });

      const mappedAccessories = addNewAccessoryInList(filteredAccessories);

      if (props.setRammeavtale) {
        let newAccessories = [];

        mappedAccessories.forEach((acc) => {
          const found = orderAccessories?.find((x) => x.key === acc.key);

          if (found) {
            acc.discountPercentage = Number(found.discountPercentage || 0);
            acc.discountAmountExclVat = Number(found.discountAmountExclVat || 0);
            acc.discountedPrice = Number(found.discountedPrice || 0);
            newAccessories.push(acc);
          } else {
            newAccessories.push(acc);
          }
        });

        setAccessoryList(
          [...newAccessories, orderAccessories?.find((x) => x?.articleType === ArticleType.DELIVERY)]
            ?.filter(Boolean)
            ?.map((item) => {
              const { id, ...rest } = item;
              return rest;
            })
        );

        props.setRammeavtale({
          agreementNumber,
          accessories: [...newAccessories, orderAccessories?.find((x) => x?.articleType === ArticleType.DELIVERY)]
            .filter(Boolean)
            .map((item) => {
              const { id, ...rest } = item;
              return rest;
            }),
        });
      }

      setShowAccessoryList(true);
      setUpdatedFromOrder(true);
      setInitialAccessories(true);
    } else if (!initialAccessories) {
      const deliveryCharge = getDeliveryCharge(props.order?.pimSnapshot);

      const filteredAccessories = props?.accessoryChanges?.filter((accessory) => {
        return (
          accessory?.key?.startsWith(VehicleConfig.ACCESSORY) ||
          accessory?.key?.startsWith(VehicleConfig.TYRE) ||
          accessory?.key?.startsWith('CUSTOM') ||
          accessory?.key?.startsWith('DARS') ||
          accessory?.key?.startsWith('FURNISHING') ||
          accessory?.key?.startsWith('SUPERSTRUCTURE') ||
          accessory?.key?.startsWith('RECONSTRUCTION')
        );
      });

      const mappedAccessories = addNewAccessoryInList(filteredAccessories);

      const combinedAccessories = [...mappedAccessories, ...deliveryCharge];

      setAccessoryList(
        combinedAccessories?.filter(Boolean)?.map((item) => {
          const { id, ...rest } = item as any;
          return rest;
        })
      );

      if (props.setRammeavtale) {
        combinedAccessories.forEach((acc) => {
          acc.discountPercentage = Number(acc.discountPercentage) || 0;
          acc.discountAmountExclVat = Number(acc.discountAmountExclVat) || 0;
        });
        props.setRammeavtale({
          agreementNumber,
          accessories: combinedAccessories?.filter(Boolean)?.map((item) => {
            const { id, ...rest } = item as any;
            return rest;
          }),
        });
      }

      setUpdatedFromOrder(true);
      setInitialAccessories(true);
    } else {
      let filteredAccessories = props?.accessoryChanges?.filter((accessory) => {
        return (
          accessory?.key?.startsWith(VehicleConfig.ACCESSORY) ||
          accessory?.key?.startsWith(VehicleConfig.TYRE) ||
          accessory?.key?.startsWith('CUSTOM') ||
          accessory?.key?.startsWith('DARS') ||
          accessory?.key?.startsWith('FURNISHING') ||
          accessory?.key?.startsWith('SUPERSTRUCTURE') ||
          accessory?.key?.startsWith('RECONSTRUCTION')
        );
      });

      let updatedAccessories = [];

      filteredAccessories.forEach((accessory) => {
        let newAccessory = {
          id: accessory?.id,
          key: accessory.key,
          name: accessory.name,
          price: accessory.price,
          discountPercentage: accessory.percentage ?? 0,
          discountedPrice: accessory.price,
          articleType: accessory?.key === VehicleConfig.TYRE ? ArticleType.WINTERTIRES : ArticleType.ACCESSORY,
          vatCode: accessory.vatCode,
        };

        const allAccessories = [...(orderAccessories || []), ...(props?.rammeavtale?.accessories || [])];
        const found = allAccessories?.find((acc) => acc?.key === accessory?.key);

        if (found && newAccessory?.articleType === ArticleType.WINTERTIRES && newAccessory?.price === found?.price) {
          newAccessory.discountPercentage = String(found.discountPercentage);
          newAccessory.discountedPrice = found.discountedPrice;
        } else if (found && newAccessory?.articleType !== ArticleType.WINTERTIRES) {
          newAccessory.discountPercentage = String(found.discountPercentage);
          newAccessory.discountedPrice = found.discountedPrice;
        }

        updatedAccessories.push(newAccessory);
      });

      let vatCalculation: ICalculatePricesBasedOnArticleListResult = null;

      vatCalculation = calculateVatForElCars(
        props.articles,
        props?.order?.pimSnapshot?.vehicleUsageCode ?? props.carType
      );

      props?.setActiveMargin({
        ...get(props, 'activeMargin', []),
        marginValue: vatCalculation?.marginAmount,
        marginPercentage: vatCalculation?.marginPercentage,
      });

      props.setVatPrices(mapVatPricesForNext(vatCalculation) as any);

      const combinedAccessories = [
        ...updatedAccessories,
        accessoryList.find((acc) => acc.articleType === ArticleType.DELIVERY),
      ].filter(Boolean);

      setAccessoryList(
        combinedAccessories.filter(Boolean).map((item) => {
          const { id, ...rest } = item;
          return rest;
        })
      );

      if (props.setRammeavtale) {
        combinedAccessories.forEach((acc) => {
          acc.discountPercentage = Number(acc.discountPercentage || 0);
          acc.discountAmountExclVat = Number(acc.discountAmountExclVat || 0);
        });
        props.setRammeavtale({
          agreementNumber,
          accessories: combinedAccessories.filter(Boolean).map((item) => {
            const { id, ...rest } = item;
            return rest;
          }),
        });
      }
    }
  }, [props.accessoryChanges, props?.order, props?.checkedAgreement]);

  const handleAgreementNumberChange = (param: string) => {
    const unFormattedNumber = param.replace(/\s/g, '');
    const formattedNumber = unFormattedNumber.replace(/(.{3})/g, '$1 ').trim();
    setAgreementNumber(formattedNumber);
    props.setRammeavtale({ agreementNumber: formattedNumber, accessories: accessoryList });
  };

  const handleValueChange = (input: string | number | Date, accessory: IAccessoryList, index: number): void => {
    const inputType = accessory?.articleType === ArticleType.DELIVERY ? InputType.INTEGER : InputType.FLOAT;
    let numericInput = Number(input);
    if (inputType === InputType.FLOAT && numericInput > 100) {
      setErrorMessages((prev) => ({
        ...prev,
        [index]: 'Rabatten kan ikke være større enn 100%',
      }));
      numericInput = 0;
      props.setIsValid(false);
    } else {
      setErrorMessages((prev) => ({
        ...prev,
        [index]: '',
      }));
      props.setIsValid(true);
    }

    const discountedPrice = calculateDiscountedPrice(accessory, numericInput, inputType);

    if (discountedPrice < 0) {
      props.setIsValid(false);
      return;
    }

    accessory.discountAmountExclVat = convertToCurrency(numericInput, accessory.price, inputType);
    accessory.discountPercentage = convertToPercentage(numericInput, accessory.price, inputType);
    accessory.discountedPrice = discountedPrice;

    if (accessory?.articleType === ArticleType.DELIVERY) {
      props.setIsValid(validateDiscount(numericInput, DropDownValues.KR));
    } else {
      props.setIsValid(validateDiscount(numericInput, DropDownValues.PERCENTAGE, 100));
    }

    const updatedAccessoryList = updateAccessoryList(accessoryList, accessory, numericInput, index);

    setAccessoryList(
      updatedAccessoryList.filter(Boolean).map((item) => {
        const { id, ...rest } = item as any;
        return rest;
      })
    );

    if (props.setRammeavtale) {
      updatedAccessoryList.forEach((acc) => {
        acc.discountPercentage = Number(acc.discountPercentage || 0);
        acc.discountAmountExclVat = Number(acc.discountAmountExclVat || 0);
      });
      props.setRammeavtale({
        agreementNumber,
        accessories: updatedAccessoryList.filter(Boolean).map((item) => {
          const { id, ...rest } = item as any;
          return rest;
        }),
      });
    }

    const updatedArticles = updateArticles(props.articles, accessory);

    let vatCalculation: ICalculatePricesBasedOnArticleListResult = null;

    vatCalculation = calculateVatForElCars(
      updatedArticles,
      props?.order?.pimSnapshot?.vehicleUsageCode ?? props.carType
    );

    props?.setActiveMargin({
      ...get(props, 'activeMargin', []),
      marginValue: vatCalculation?.marginAmount,
      marginPercentage: vatCalculation?.marginPercentage,
    });

    props.setVatPrices(mapVatPricesForNext(vatCalculation) as any);

    props.onUpdateArticles(accessory);
  };

  const getAccessoryTag = (key: string): string => {
    if (key.startsWith('CUSTOM')) {
      return 'Egendefinert';
    }

    if (key.startsWith('FURNISHING')) {
      return 'Innredning';
    }

    if (key.startsWith('SUPERSTRUCTURE')) {
      return 'Påbygg';
    }

    if (key.startsWith('RECONSTRUCTION')) {
      return 'Ombygging';
    }

    return 'DARS';
  };

  const deleteRammeavtale = () => {
    let updatedAccessoryList = [...accessoryList];

    updatedAccessoryList.forEach((accessory) => {
      accessory.discountAmountExclVat = 0;
      accessory.discountPercentage = 0;
      accessory.discountedPrice = 0;
    });

    setAccessoryList(
      updatedAccessoryList.filter(Boolean).map((item) => {
        const { id, ...rest } = item as any;
        return rest;
      })
    );

    if (props.setRammeavtale) {
      props.setRammeavtale(null);
    }

    let updatedArticles = [...props.articles];

    updatedArticles.forEach((article) => {
      article.discountAmountExclVat = 0;
      article.discountPercentage = 0;
      if (article.key === VehicleConfig.DELIVERY_CHARGE) {
        delete article.discountPercentage;
      } else {
        delete article.discountAmountExclVat;
      }
    });

    let discountArticles = articlesForDiscount(updatedArticles, {}, props.order);

    let vatCalculation: ICalculatePricesBasedOnArticleListResult = null;

    vatCalculation = calculateVatForElCars(
      discountArticles,
      props?.order?.pimSnapshot?.vehicleUsageCode ?? props.carType
    );

    props?.setActiveMargin({
      ...get(props, 'activeMargin', []),
      marginValue: vatCalculation?.marginAmount,
      marginPercentage: vatCalculation?.marginPercentage,
    });

    props.setVatPrices(mapVatPricesForNext(vatCalculation) as any);

    props.onUpdateArticles(null, true, discountArticles);

    setShowAccessoryList(false);
    setAgreementNumber(null);
  };

  return (
    <>
      {!showAccessoryList && (
        <div className="flex gap-4 items-end">
          <InputField
            id="numberInput"
            label="Rammeavtalenummer"
            type={InputType.TEXT_LINE}
            onChange={(input: string | number) => handleAgreementNumberChange(input as string)}
            className="flex-1 w-full"
            defaultValue={agreementNumber?.toString() || ''}
          />
          <Button
            className="[&:disabled]:bg-neutralGrey [&:disabled]:text-neutralPlus [&:disabled]:opacity-100 flex-1"
            variant="purple"
            disabled={!agreementNumber}
            onClick={() => setShowAccessoryList(!showAccessoryList)}
          >
            Lagre
          </Button>
        </div>
      )}
      {showAccessoryList && (
        <div className="flex flex-col gap-8">
          <div className="bg-neutralGrey h-px w-full"></div>
          <div className="flex gap-6 items-center">
            <h3 className="text-lg font-extrabold leading-[175%] flex-1">
              Rammeavtale {agreementNumber?.replace(/\s/g, '-')}
            </h3>
            <Button variant="red" className="h-8 py-1 px-2 gap-2 font-extrabold" onClick={deleteRammeavtale}>
              <Icon icon={IconType.NewNextTrash} fill="white" height={24} width={24} />
              Slett
            </Button>
          </div>
          {accessoryList?.map((accessory, index) => {
            return (
              <div key={accessory?.key} className="flex flex-col gap-8">
                <div className="flex gap-8">
                  <div className="flex-1 h-fit">
                    <p className="text-base font-extrabold leading-[175%]">{accessory?.name}</p>
                    {accessory.discountedPrice !== accessory.price ? (
                      <p className="text-sm font-semibold leading-[170%]">
                        Pris eks. mva:{' '}
                        <del className="text-sm font-semibold leading-[170%]">{formatNumber(accessory?.price)} kr</del>
                        <span className="text-sm font-extrabold leading-[170%] ml-2">
                          {formatNumber(accessory?.discountedPrice)} kr
                        </span>
                      </p>
                    ) : (
                      <p className="text-sm font-semibold leading-[170%]">
                        Pris eks. mva: {formatNumber(accessory?.price)} kr
                      </p>
                    )}
                    {accessory?.articleType !== ArticleType.DELIVERY && (
                      <Tag severity={TTagSeverity.NEUTRAL} className="py-px">
                        <span className="text-sm/[170%] font-semibold">
                          {getAccessoryTag(accessory?.key) || 'test'}
                        </span>
                      </Tag>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 max-w-[11.25rem]">
                    {accessory?.key === VehicleConfig.DELIVERY_CHARGE ? (
                      <InputField
                        id={accessory?.key}
                        label="Legg til rabatt"
                        value={accessory?.discountAmountExclVat?.toString()}
                        maxLength={formatNumber(accessory?.price)?.length}
                        onChange={(value: string | number) => handleValueChange(value, accessory, index)}
                        type={InputType.INTEGER}
                        endAdornment={{ content: 'kr' }}
                        disabled={!accessory?.price}
                      />
                    ) : (
                      <InputField
                        id={accessory?.key}
                        label="Legg til rabatt"
                        value={accessory?.discountPercentage?.toString()}
                        onChange={(value: string | number) => handleValueChange(value, accessory, index)}
                        maxLength={3}
                        type={InputType.INTEGER}
                        endAdornment={{ content: '%' }}
                        disabled={!accessory?.price}
                        errorMessage={errorMessages[index] || ''}
                      />
                    )}
                  </div>
                </div>
                <div className="bg-neutralGrey h-px w-full"></div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
